import pluralize from "pluralize";

/** The info box lists two flags and summarises the rest on the overflow line. */
export const INFO_BOX_VISIBLE_FLAG_COUNT = 2;

export const CLIENT_FLAGS_SECTION_TITLE = "Flags";

/** Shown beside every client flags heading, so providers know the portal never shows a flag. */
export const CLIENT_FLAG_VISIBILITY_MESSAGE =
  "Flags are visible to your practice only. Clients can't see them.";

/** Shown in place of the priority once a flag's library entry has been deleted. */
export const DEPRECATED_FLAG_LABEL = "Deprecated";

export const MANAGE_CLIENT_FLAGS_LABEL = "Manage";

export const NO_CLIENT_FLAGS_ADDED_MESSAGE = "No flags added";

/** Takes the place of `Manage` while a client has no flags. */
export const ADD_CLIENT_FLAGS_LABEL = "+ Add Flags";

/** `+2 more · 1 Medium, 1 Low` — the terms are joined by this, the count by the separator below. */
export const OVERFLOW_TERM_SEPARATOR = ", ";

export const OVERFLOW_COUNT_SEPARATOR = " · ";

export const ACTIVE_FLAGS_HEADING = (count: number) =>
  `${count} Active ${pluralize("flag", count)}`;

export const ADD_A_FLAG_HEADING = "Add a flag";

export const CLIENT_FLAG_SEARCH_PLACEHOLDER = "Search flags";

export const SAVE_CLIENT_FLAGS_LABEL = "Save";

export const CLIENT_FLAGS_SAVED_MESSAGE = "New flags have been added!";

export const NO_ACTIVE_CLIENT_FLAGS_MESSAGE = "No flags have been added to this client yet.";

export const NO_LIBRARY_FLAGS_LEFT_MESSAGE = "Every flag in the library is already applied.";

export const NO_MATCHING_CLIENT_FLAGS_MESSAGE = "No flags match that search.";

export const clientFlagsDrawerTitle = (firstName: string) => `Flags for ${firstName}`;

export const CLIENT_FLAGS_COLUMN_HEADING = "Flags";

export const CLIENT_FLAGS_POPOVER_TITLE = "Flags";

export const MANAGE_CLIENT_FLAGS_ACTION = "Manage Flags";

export const NO_CLIENT_FLAGS_POPOVER_MESSAGE = "No flags on this client yet.";

export const REMOVE_CLIENT_FLAG_REASON_REQUIRED_MESSAGE = "Select a reason";

export const REMOVE_CLIENT_FLAG_OTHER_REASON_REQUIRED_MESSAGE = "Enter a reason";

/** Why a flag came off a chart. `Other` asks for the reason in the clinician's own words. */
export const CLIENT_FLAG_REMOVAL_REASON = {
  ConditionResolved: "condition-resolved",
  NoLongerApplicable: "no-longer-applicable",
  AddedInError: "added-in-error",
  WrongClient: "entered-on-wrong-client",
  Other: "other",
} as const;

export type ClientFlagRemovalReason =
  (typeof CLIENT_FLAG_REMOVAL_REASON)[keyof typeof CLIENT_FLAG_REMOVAL_REASON];

export const CLIENT_FLAG_REMOVAL_REASON_DISPLAY: Record<ClientFlagRemovalReason, string> = {
  [CLIENT_FLAG_REMOVAL_REASON.ConditionResolved]: "Condition resolved",
  [CLIENT_FLAG_REMOVAL_REASON.NoLongerApplicable]: "No longer applicable",
  [CLIENT_FLAG_REMOVAL_REASON.AddedInError]: "Added in error",
  [CLIENT_FLAG_REMOVAL_REASON.WrongClient]: "Entered on wrong client",
  [CLIENT_FLAG_REMOVAL_REASON.Other]: "Other",
};

export const CLIENT_FLAG_REMOVAL_REASON_OPTIONS = Object.values(CLIENT_FLAG_REMOVAL_REASON).map(
  (value) => ({ value, label: CLIENT_FLAG_REMOVAL_REASON_DISPLAY[value] }),
);

export const CLIENT_FLAG_REMOVED_MESSAGE = (name: string) =>
  `${name} was removed from this client's chart.`;

export const FLAG_ARCHIVE_TAB_LABEL = "Flag Archive";

export const FLAG_ARCHIVE_CARD_TITLE = "Flags";

export const NO_ARCHIVED_CLIENT_FLAGS_MESSAGE = "No archived flags.";

export const FLAG_FILTER_DRAWER_TITLE = "Filter by Flags";

export const FLAG_FILTER_RESET_LABEL = "Reset";

export const FLAG_FILTER_APPLY_LABEL = "Apply";

export const NO_FILTERABLE_FLAGS_MESSAGE = "The flag library has no flags yet.";

/** `Filter Flags` with nothing applied; `Filter Flags (2)` while two flags filter the list. */
export const filterFlagsButtonLabel = (appliedCount: number) =>
  appliedCount > 0 ? `Filter Flags (${appliedCount})` : "Filter Flags";
